import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Calendar } from './ui/calendar';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const audienceLabel = (audience) => ({
  teachers: 'Teachers',
  parents: 'Parents',
  both: 'Everyone',
}[audience] || audience);

const categoryLabel = (category) => ({
  school_event: 'School Event',
  training: 'Training',
  activity: 'Activity',
  public_holiday: 'Holiday',
  team_bonding: 'Team Bonding',
  meeting: 'Meeting',
  birthday_celebration: 'Birthday Celebration',
  sstck_public_holiday: 'SSTCK Public Holiday',
  sstcnt_public_holiday: 'SSTCNT Public Holiday',
}[category] || category);

const categoryColor = (category) => ({
  school_event: 'bg-blue-100 text-blue-700 border-blue-300',
  training: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  activity: 'bg-violet-100 text-violet-700 border-violet-300',
  public_holiday: 'bg-orange-100 text-orange-700 border-orange-300',
  team_bonding: 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-300',
  meeting: 'bg-amber-100 text-amber-700 border-amber-300',
  birthday_celebration: 'bg-pink-100 text-pink-700 border-pink-300',
  sstck_public_holiday: 'bg-orange-50 text-orange-700 border-orange-200',
  sstcnt_public_holiday: 'bg-orange-50 text-orange-700 border-orange-200',
}[category] || 'bg-slate-100 text-slate-700 border-slate-300');

const formatMonth = (monthString) => {
  const [year, month] = monthString.split('-');
  if (!year || !month) return monthString;
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
};

const toMonthString = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

const CalendarEventsSection = ({ isAdmin = false }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [form, setForm] = useState({
    title: '',
    description: '',
    date: '',
    audience: 'both',
    category: 'school_event',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/api/calendar-events`, {
          params: { month },
          withCredentials: true,
        });
        setEvents(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error(error);
        toast.error('Unable to load calendar events');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [month]);

  const eventDates = useMemo(() => {
    return events
      .map((event) => {
        if (!event?.date) return null;
        const parsed = new Date(event.date);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      })
      .filter(Boolean);
  }, [events]);

  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  }, [events]);

  const handleSaveEvent = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/api/calendar-events`, form, { withCredentials: true });
      toast.success('Event created');
      setForm({ title: '', description: '', date: '', audience: 'both', category: 'school_event' });
      const { data } = await axios.get(`${API}/api/calendar-events`, {
        params: { month },
        withCredentials: true,
      });
      setEvents(Array.isArray(data) ? data : []);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to create event';
      toast.error(typeof msg === 'string' ? msg : 'Failed to create event');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!eventId) return;
    if (!window.confirm('Delete this event? This action cannot be undone.')) return;

    try {
      await axios.delete(`${API}/api/calendar-events/${eventId}`, { withCredentials: true });
      toast.success('Event deleted');
      setEvents((prevEvents) => prevEvents.filter((event) => event.id !== eventId));
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to delete event';
      toast.error(typeof msg === 'string' ? msg : 'Failed to delete event');
    }
  };

  const handleChangeMonth = (direction) => {
    const current = new Date(`${month}-01`);
    const updated = new Date(current.getFullYear(), current.getMonth() + direction, 1);
    setMonth(toMonthString(updated));
  };

  return (
    <Card className="bg-white border-slate-200">
      <CardHeader className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            School Calendar
          </CardTitle>
          <CardDescription>Month view of public holidays, training and school events.</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => handleChangeMonth(-1)}>
            Previous
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleChangeMonth(1)}>
            Next
          </Button>
          {isAdmin && (
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}>
              Add Event
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        <div className="grid gap-6 lg:grid-cols-[minmax(280px,350px)_1fr]">
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
              <p className="text-sm text-slate-600">Viewing</p>
              <p className="text-xl font-semibold text-slate-900">{formatMonth(month)}</p>
              <p className="text-sm text-slate-500">Tap any highlighted day to see events for the month below.</p>
            </div>
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <Calendar
                month={new Date(`${month}-01`)}
                selected={eventDates}
                modifiers={{ event: eventDates }}
                modifiersClassNames={{ event: 'bg-blue-600 text-white' }}
                classNames={{ caption_label: 'text-base font-semibold' }}
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-sm text-slate-500">Events this month</p>
                <p className="text-2xl font-semibold text-slate-900">{sortedEvents.length}</p>
              </div>
              <Badge className="bg-slate-100 text-slate-800 border-slate-200">{events.filter((e) => e.category && e.category.includes('public_holiday')).length} Holidays</Badge>
            </div>
            <div className="space-y-3">
              {loading ? (
                <div className="text-sm text-slate-500">Loading events...</div>
              ) : sortedEvents.length === 0 ? (
                <div className="text-sm text-slate-500">No events found for this month.</div>
              ) : (
                sortedEvents.map((event) => (
                  <div key={event.id} className="p-4 border border-slate-200 rounded-xl bg-white">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-base font-semibold text-slate-900">{event.title}</p>
                        <p className="text-sm text-slate-500">{new Date(event.date).toLocaleDateString()}</p>
                      </div>
                      <Badge className={`${categoryColor(event.category)} border`}>
                        {categoryLabel(event.category)}
                      </Badge>
                    </div>
                    <p className="mt-3 text-sm text-slate-600 whitespace-pre-line">{event.description || 'No description provided.'}</p>
                    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                      <span>{audienceLabel(event.audience)}</span>
                      <span>•</span>
                      <span>By {event.author || 'Admin'}</span>
                    </div>
                    {isAdmin && (
                      <div className="mt-4 flex justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600"
                          onClick={() => handleDeleteEvent(event.id)}
                          data-testid={`delete-calendar-event-${event.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </Button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {isAdmin && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Create calendar event</h3>
              <p className="text-sm text-slate-600">Add a school event, training or activity that teachers and parents can see.</p>
            </div>
            <form onSubmit={handleSaveEvent} className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Title</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                  required
                />
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))}
                  required
                />
              </div>
              <div className="md:col-span-2">
                <Label>Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                  rows={3}
                />
              </div>
              <div>
                <Label>Audience</Label>
                <select
                  value={form.audience}
                  onChange={(e) => setForm((prev) => ({ ...prev, audience: e.target.value }))}
                  className="mt-2 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="both">Everyone</option>
                  <option value="teachers">Teachers</option>
                  <option value="parents">Parents</option>
                </select>
              </div>
              <div>
                <Label>Category</Label>
                <select
                  value={form.category}
                  onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
                  className="mt-2 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="school_event">School Event</option>
                  <option value="training">Training</option>
                  <option value="activity">Activity</option>
                  <option value="team_bonding">Team Bonding</option>
                  <option value="meeting">Meeting</option>
                  <option value="birthday_celebration">Birthday Celebration</option>
                  <option value="public_holiday">Holiday</option>
                  <option value="sstck_public_holiday">SSTCK Public Holiday</option>
                  <option value="sstcnt_public_holiday">SSTCNT Public Holiday</option>
                </select>
              </div>
              <div className="md:col-span-2 flex justify-end">
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={saving}>
                  {saving ? 'Saving…' : 'Create Event'}
                </Button>
              </div>
            </form>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CalendarEventsSection;
